/**
 * The resolved plugin set for an organization.
 *
 * `orgPluginLoader.ts` imports the plugin chunks an organization has enabled
 * and publishes them here; every other consumer only reads. Keeping the
 * registry out of this module keeps the plugin graph acyclic — plugin modules
 * import host hook descriptors, descriptors read from here, and only the loader
 * depends on `registry` — which is also what lets a plugin hot update replace
 * the loader without discarding what has already been resolved.
 *
 * `AuthUserProvider` loads the active chunks before authenticated routes are
 * built so synchronous consumers (`getActivePlugins`) can read contributions.
 */
import { useSyncExternalStore } from "react";

import { useOrganization } from "@features/organizations/data/organizationQueries";

import type { BrandhubPlugin } from "./types";

type OrgLike = { id: string; plugins: string[] };

export type { OrgLike };

export type ActivePluginSet = {
  plugins: readonly BrandhubPlugin[];
  /**
   * Changes on every publication, even when plugin keys stay the same during
   * HMR. The router uses this identity to replace its complete route manifest.
   */
  revision: number;
  /**
   * Stable (org id + sorted plugin keys) signature this set was loaded for.
   * Bootstrap ignores a published set that does not match the current org so a
   * cached query cannot build routes from a previous enablement.
   */
  enablementKey: string;
};

const activePluginsByKey = new Map<string, ActivePluginSet>();
const listeners = new Set<() => void>();
let nextRevision = 1;

/**
 * Identity of a published plugin set for an organization. Keyed by org id so
 * readers (e.g. `useOrganization` after a focus refetch) still resolve even when
 * the `plugins` array order or payload differs from the load-time org. Changing
 * enablement republishes into the same slot via the loader. Each published set
 * is also tagged with `enablementKey` so bootstrap can ignore a stale snapshot.
 */
function getActivePluginsKey(organization: OrgLike): string {
  return organization.id;
}

/** Stable signature of which plugin keys an org has enabled (order-independent). */
export function getPluginEnablementKey(organization: OrgLike): string {
  return JSON.stringify([organization.id, [...organization.plugins].sort()]);
}

export function pluginSetMatchesOrganization(
  pluginSet: ActivePluginSet,
  organization: OrgLike
): boolean {
  return pluginSet.enablementKey === getPluginEnablementKey(organization);
}

/**
 * Publishes a resolved plugin set and re-renders both UI contribution readers
 * and the root router bootstrap subscriber.
 */
export function publishActivePlugins(
  organization: OrgLike,
  plugins: readonly BrandhubPlugin[]
): void {
  activePluginsByKey.set(getActivePluginsKey(organization), {
    plugins,
    revision: nextRevision,
    enablementKey: getPluginEnablementKey(organization),
  });
  nextRevision += 1;

  for (const listener of listeners) {
    listener();
  }
}

export function getActivePlugins(
  organization: OrgLike
): readonly BrandhubPlugin[] {
  const pluginSet = activePluginsByKey.get(getActivePluginsKey(organization));
  if (!pluginSet) {
    throw new Error(
      `Active plugins have not been loaded for organization "${organization.id}".`
    );
  }

  return pluginSet.plugins;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function useActivePluginSet(
  organization?: OrgLike
): ActivePluginSet | undefined {
  return useSyncExternalStore(subscribe, () =>
    organization
      ? activePluginsByKey.get(getActivePluginsKey(organization))
      : undefined
  );
}

/**
 * The current org's active plugins. Subscribed so that republishing — which in
 * practice only happens when the dev server hot-swaps a plugin — re-renders the
 * slots that plugin contributes to.
 */
export function useActivePlugins(): readonly BrandhubPlugin[] {
  const organization = useOrganization();
  const pluginSet = useActivePluginSet(organization);

  if (!pluginSet) {
    throw new Error(
      `Active plugins have not been loaded for organization "${organization.id}".`
    );
  }

  return pluginSet.plugins;
}

// Dev only. Published plugin sets cannot survive this module being
// re-executed: React Query still reports the load as finished, so
// `getActivePlugins` would throw for plugins nothing is going to load again. A
// hot update that reaches this module therefore reloads the page. Plugin edits
// do not reach it — `orgPluginLoader.ts` accepts those and republishes.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload();
  });
}
